import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Crown, CreditCard, Shield, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useGame } from "@/context/GameContext";
import { toast } from "sonner";
import { useParams } from "react-router-dom";

export default function PaywallModal({ isOpen, onClose, layerId }) {
  const { language, initiateMidtransPayment, syncAccess, parentId, purchaseLayerSimulated, purchaseFullAccessSimulated } = useGame();
  const { age } = useParams();
  const layerNum = layerId || parseInt(age, 10) || 1;

  const isProduction = process.env.REACT_APP_MIDTRANS_IS_PRODUCTION === "True" || process.env.REACT_APP_MIDTRANS_IS_PRODUCTION === "true";
  const showSimulation = !isProduction;

  const handleBuyLayerSimulated = async () => {
    const success = await purchaseLayerSimulated(layerNum);
    if (success) {
      toast.success(
        language === "id"
          ? `[Simulasi UAT] Berhasil membuka Layer ${layerNum} 🎉`
          : `[UAT Simulation] Successfully unlocked Layer ${layerNum} 🎉`
      );
      if (parentId) await syncAccess(parentId);
      onClose();
    } else {
      toast.error(
        language === "id"
          ? "Gagal mensimulasikan pembayaran."
          : "Failed to simulate payment."
      );
    }
  };

  const handleBuyFullSimulated = async () => {
    const success = await purchaseFullAccessSimulated();
    if (success) {
      toast.success(
        language === "id"
          ? "[Simulasi UAT] Berhasil membuka seluruh Layer 1-5 🎉"
          : "[UAT Simulation] Successfully unlocked all Layers 1-5 🎉"
      );
      if (parentId) await syncAccess(parentId);
      onClose();
    } else {
      toast.error(
        language === "id"
          ? "Gagal mensimulasikan pembayaran."
          : "Failed to simulate payment."
      );
    }
  };

  const handleBuyLayer = async () => {
    const res = await initiateMidtransPayment("layer", layerNum);
    if (res && res.token) {
      if (window.snap) {
        window.snap.pay(res.token, {
          onSuccess: async function (result) {
            toast.success(
              language === "id"
                ? `Selamat! Anda berhasil membuka Layer ${layerNum} 🎉`
                : `Congratulations! You successfully unlocked Layer ${layerNum} 🎉`
            );
            if (parentId) await syncAccess(parentId);
            onClose();
          },
          onPending: function (result) {
            toast.info(
              language === "id"
                ? "Menunggu pembayaran. Silakan selesaikan transaksi Anda."
                : "Payment pending. Please complete your transaction."
            );
          },
          onError: function (result) {
            toast.error(
              language === "id"
                ? "Pembayaran gagal!"
                : "Payment failed!"
            );
          },
          onClose: function () {
            toast.info(
              language === "id"
                ? "Transaksi dibatalkan."
                : "Transaction cancelled."
            );
          }
        });
      } else {
        window.open(res.redirect_url, "_blank");
      }
    } else {
      toast.error(
        language === "id"
          ? "Gagal memproses pembayaran. Silakan coba lagi."
          : "Failed to process purchase. Please try again."
      );
    }
  };

  const handleBuyFull = async () => {
    const res = await initiateMidtransPayment("full", null);
    if (res && res.token) {
      if (window.snap) {
        window.snap.pay(res.token, {
          onSuccess: async function (result) {
            toast.success(
              language === "id"
                ? "Selamat! Anda sekarang memiliki akses FULL ke seluruh Game & Layer 🎉"
                : "Congratulations! You now have FULL access to all Games & Layers 🎉"
            );
            if (parentId) await syncAccess(parentId);
            onClose();
          },
          onPending: function (result) {
            toast.info(
              language === "id"
                ? "Menunggu pembayaran. Silakan selesaikan transaksi Anda."
                : "Payment pending. Please complete your transaction."
            );
          },
          onError: function (result) {
            toast.error(
              language === "id"
                ? "Pembayaran gagal!"
                : "Payment failed!"
            );
          },
          onClose: function () {
            toast.info(
              language === "id"
                ? "Transaksi dibatalkan."
                : "Transaction cancelled."
            );
          }
        });
      } else {
        window.open(res.redirect_url, "_blank");
      }
    } else {
      toast.error(
        language === "id"
          ? "Gagal memproses pembayaran. Silakan coba lagi."
          : "Failed to process purchase. Please try again."
      );
    }
  };

  const features = language === "id"
    ? [
        "Akses semua 15+ Game Edukasi Premium",
        "Laporan Grafik & Analisis Akurasi Tumbuh Kembang",
        "Rekomendasi Aktivitas Fisik berbasis Jurnal Ilmiah",
        "Bermain Tanpa Batas tanpa batasan harian",
        "Dukungan multi-profil untuk hingga 3 Anak",
        "100% Bebas Iklan & Aman untuk si kecil"
      ]
    : [
        "Access all 15+ Premium Educational Games",
        "Detailed Growth Charts & Accuracy Analysis",
        "Evidence-based Physical Activities from Scientific Journals",
        "Unlimited Playtime without daily restrictions",
        "Multi-profile support for up to 3 Children",
        "100% Ad-Free & Safe for toddlers"
      ];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto">
          {/* Main Modal Container */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 30 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 30 }}
            transition={{ type: "spring", damping: 15 }}
            className="relative w-full max-w-xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden border-4 border-amber-300 my-4 flex flex-col max-h-[90vh]"
          >
            {/* Background elements */}
            <div className="absolute top-0 right-0 w-36 h-36 bg-amber-100 rounded-full blur-3xl opacity-60 pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-44 h-44 bg-sky-100 rounded-full blur-3xl opacity-60 pointer-events-none" />

            {/* Close Button (Sticky) */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 transition-colors z-20 text-slate-400 hover:text-slate-600 bg-white/50 backdrop-blur-sm"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Scrollable Content Area */}
            <div className="p-5 sm:p-8 overflow-y-auto relative z-10 w-full h-full scrollbar-none">
              {/* Premium Badge Header */}
              <div className="text-center mb-6 mt-2 relative">
              <motion.div
                animate={{ rotate: [0, 8, -8, 0], scale: [1, 1.05, 1] }}
                transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-400 to-amber-500 text-white font-display font-bold px-5 py-2 rounded-full shadow-lg border-2 border-white text-sm"
              >
                <Crown className="w-4 h-4 fill-white" />
                BABYLABS PREMIUM
              </motion.div>

              <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-800 mt-4 leading-snug">
                {language === "id" ? "Buka Seluruh Dunia Petualangan!" : "Unlock a Whole World of Adventure!"}
              </h2>
              <p className="text-slate-500 text-sm font-bold mt-1 px-4">
                {language === "id"
                  ? "Dukung milestone perkembangan si kecil dengan akses penuh tanpa batas."
                  : "Support your child's developmental milestones with full unlimited access."}
              </p>
            </div>

            {/* Features list */}
            <div className="bg-slate-50/70 border border-slate-100 rounded-3xl p-4 sm:p-5 mb-6 space-y-2.5">
              {features.map((feat, idx) => (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.04 }}
                  key={idx}
                  className="flex items-start gap-3"
                >
                  <div className="w-5 h-5 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-emerald-600" strokeWidth={3} />
                  </div>
                  <span className="text-slate-600 font-bold text-sm sm:text-[14px]">
                    {feat}
                  </span>
                </motion.div>
              ))}
            </div>

            {/* Pricing Section (Tiered) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Option 1: Current Layer */}
              <div className="flex flex-col justify-between border-2 border-slate-200 rounded-3xl p-5 hover:border-slate-300 transition-all bg-white relative">
                <div>
                  <div className="text-[10px] font-extrabold text-slate-500 tracking-wider uppercase mb-1">
                    {language === "id" ? `Hanya Layer ${layerNum}` : `Layer ${layerNum} Only`}
                  </div>
                  <h3 className="font-display font-bold text-lg text-slate-800 mb-2 leading-tight">
                    {language === "id" ? `Buka Layer ${layerNum}` : `Unlock Layer ${layerNum}`}
                  </h3>
                  <p className="text-xs text-slate-500 font-bold mb-4">
                    {language === "id" 
                      ? `Membuka semua game seru & aktivitas stimulasi khusus usia ${layerNum} tahun.` 
                      : `Unlocks all games & specific developmental stimulation for ${layerNum} year olds.`}
                  </p>
                </div>
                <div>
                  <div className="font-display text-2xl font-extrabold text-slate-800 mb-4">
                    Rp 79.000
                  </div>
                  <Button
                    onClick={handleBuyLayer}
                    className="w-full h-11 rounded-2xl bg-slate-700 hover:bg-slate-800 text-white font-display text-sm font-bold shadow-[0_4px_0_#334155] hover:shadow-[0_2px_0_#334155] hover:translate-y-[2px] transition-all"
                  >
                    {language === "id" ? "Beli Layer Ini 🚀" : "Buy This Layer 🚀"}
                  </Button>
                  {showSimulation && (
                    <button
                      onClick={handleBuyLayerSimulated}
                      className="w-full mt-2 text-xs font-bold text-slate-500 hover:text-slate-700 underline text-center cursor-pointer"
                      data-testid="simulate-buy-layer"
                    >
                      {language === "id" ? "Simulasi Pembayaran (UAT) 🧪" : "Simulate Payment (UAT) 🧪"}
                    </button>
                  )}
                </div>
              </div>

              {/* Option 2: Full Access (RECOMMENDED) */}
              <div className="flex flex-col justify-between border-2 border-amber-300 bg-amber-50/50 rounded-3xl p-5 hover:border-amber-400 transition-all relative overflow-hidden shadow-md">
                <div className="absolute top-2 right-2 bg-amber-500 text-white text-[9px] font-extrabold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm">
                  <Star className="w-2.5 h-2.5 fill-white" />
                  RECOMMENDED
                </div>
                <div>
                  <div className="text-[10px] font-extrabold text-amber-600 tracking-wider uppercase mb-1">
                    {language === "id" ? "Semua Game & Layer" : "Full Access Bundle"}
                  </div>
                  <h3 className="font-display font-bold text-lg text-slate-800 mb-2 leading-tight">
                    {language === "id" ? "Full Access 1-5" : "Full Access 1-5"}
                  </h3>
                  <p className="text-xs text-slate-600 font-bold mb-4">
                    {language === "id" 
                      ? "Membuka seluruh 15+ game edukasi premium dan semua update di masa depan selamanya!" 
                      : "Unlocks all 15+ premium educational games and all future updates forever!"}
                  </p>
                </div>
                <div>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="font-display text-2xl font-extrabold text-slate-800">
                      Rp 199.000
                    </span>
                    <span className="text-slate-400 font-bold text-xs line-through">
                      Rp 395.000
                    </span>
                  </div>
                  <Button
                    onClick={handleBuyFull}
                    className="w-full h-11 rounded-2xl bg-amber-500 hover:bg-amber-400 text-white font-display text-sm font-bold shadow-[0_4px_0_#d97706] hover:shadow-[0_2px_0_#d97706] hover:translate-y-[2px] transition-all"
                  >
                    {language === "id" ? "Buka Semua Game 👑" : "Unlock All Games 👑"}
                  </Button>
                  {showSimulation && (
                    <button
                      onClick={handleBuyFullSimulated}
                      className="w-full mt-2 text-xs font-bold text-amber-600 hover:text-amber-700 underline text-center cursor-pointer"
                      data-testid="simulate-buy-full"
                    >
                      {language === "id" ? "Simulasi Pembayaran (UAT) 🧪" : "Simulate Payment (UAT) 🧪"}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Footer Trust badging */}
            <div className="flex flex-col items-center gap-4 pt-4 pb-2">
              <button 
                onClick={onClose}
                className="text-slate-400 font-bold text-sm hover:text-slate-600 underline underline-offset-2"
              >
                {language === "id" ? "Nanti Saja" : "Maybe Later"}
              </button>
              
              <div className="flex items-center justify-center gap-6 text-[10px] font-bold text-slate-400">
                <div className="flex items-center gap-1">
                  <Shield className="w-3.5 h-3.5 text-slate-400" />
                  {language === "id" ? "Pembayaran Aman" : "Secure Payment"}
                </div>
                <div className="flex items-center gap-1">
                  <CreditCard className="w-3.5 h-3.5 text-slate-400" />
                  {language === "id" ? "Sekali Bayar Selamanya" : "One-Time Purchase"}
                </div>
              </div>
            </div>
            
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
